import React, { createContext, useContext, useMemo, useState } from 'react';
import { FeatureGateEnvironment } from '@atlaskit/feature-gate-js-client';
import { FeatureGatesInitialization } from '@atlassian/feature-gates-react';
import Cookie from 'js-cookie';
import { AJS_ANONYMOUS_ID_KEY, getEnvironment } from '@app/common';
import SentryLogger from '@app/sentry';
import { useUserContext } from '@app/auth';

const FeatureGatesReadyContext = createContext<boolean>(false);

/**
 * Returns true once the Statsig feature gate client has finished initializing.
 * Use this to avoid acting on gate values before they are known.
 */
export function useFeatureGatesReady(): boolean {
  return useContext(FeatureGatesReadyContext);
}

const FEATURE_GATE_TARGET_APP: string = 'community-core_web';

/**
 * Maps the application environment to the FeatureGateEnvironment enum.
 */
export function getFeatureGateEnvironment(): FeatureGateEnvironment {
  const env = getEnvironment();

  switch (env) {
    case 'production':
      return FeatureGateEnvironment.Production;
    case 'staging':
      return FeatureGateEnvironment.Staging;
    case 'development':
    default:
      return FeatureGateEnvironment.Development;
  }
}

/**
 * Initializes feature gates for the application.
 * Wraps FeatureGatesInitialization from @atlassian/feature-gates-react so
 * that src/index.tsx stays a clean list of providers.
 * Requires UserProvider to be rendered above this component.
 */
export function FeatureGateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isReady, setIsReady] = useState(false);
  const { user, isSettled } = useUserContext();

  const identifiers = useMemo(
    () => ({
      analyticsAnonymousId: Cookie.get(AJS_ANONYMOUS_ID_KEY),
      atlassianAccountId: user?.account_id,
    }),
    [user],
  );

  // Don't mount FeatureGatesInitialization until the user fetch has settled.
  // Mounting it before the user is known causes a double-init that throws
  // "Client must be initialized before using this method" and makes checkGate()
  // return false, incorrectly triggering a redirect.
  if (!isSettled) {
    return null;
  }

  function handleReady(err?: Error) {
    if (err) {
      SentryLogger.error(err, {
        extra: { message: '[FeatureGates] Initialization failed' },
      });
    }
    // Mark gates as ready even on error so the app can proceed
    // (fg() returns false on error, which is the safe default)
    setIsReady(true);
  }

  return (
    <FeatureGatesReadyContext.Provider value={isReady}>
      <FeatureGatesInitialization
        identifiers={identifiers}
        options={{
          apiKey: process.env.REACT_APP_STATSIG_API_KEY ?? '',
          environment: getFeatureGateEnvironment(),
          targetApp: FEATURE_GATE_TARGET_APP,
        }}
        onReady={handleReady}
      >
        {children}
      </FeatureGatesInitialization>
    </FeatureGatesReadyContext.Provider>
  );
}
